/**
 * The "Equipment & Process" section (and a few like it) of the capability, solution and service
 * pages: a list of slides, each a photo beside a heading, paragraphs, up to two bullet lists
 * ("Benefits", "Capabilities") and a call-to-action link (typically "Let's Discuss Your Project"
 * → `/contact-us` or `/contact-us#quoteRequest`).
 *
 * Rendered by `../BasePage/BasePage.tsx` and 16 pages under `pages/metal-solutions/` (the drilled
 * bar, sawing, tube laser, burning and laser cutting capability pages, the Ryerson solutions
 * pages, and the Southern Tool Steel services pages), always as an `AllSections` entry. Content
 * modules type their copy with {@link TextImageSlideProps} (for example
 * `src/content/ryerson/metal-solutions/capabilities/bar-tube-structural-processing/sawing.ts` →
 * `EquipmentAndProcessContent`), so a change to these types reaches all of them.
 *
 * From `lg` up it renders `./Desktop/TextImageSlideDesktop.tsx` (the title as an `h1`, then
 * slides alternating text-left and text-right); below `lg`, `./Mobile/TextImageSlideMobile.tsx`
 * (stacked slides, no title: the `AllSections` accordion tab shows it).
 */
import React from "react";
import { Media } from "@ryerson/frontend.next";
import TextImageSlideMobile from "./Mobile/TextImageSlideMobile";
import TextImageSlideDesktop from "./Desktop/TextImageSlideDesktop";

/** One bullet: an optional bold lead-in, then the text. */
export type Bullet = {
	bold?: string;
	normal: string;
};

/** A titled bullet list. */
export type BulletPoints = {
	/**
	 * List heading. From `lg` up an empty string still takes a line (a non-breaking space), so
	 * the two lists' bullets stay aligned.
	 */
	title: string;
	bullet: Bullet[];
};

/** One slide. */
export type Section = {
	/** Slide heading; also the React key and the image's `alt` text, so keep it unique. */
	title: string;
	/** First paragraph. */
	description: string;
	/**
	 * Second paragraph. From `lg` up it is shown; below `lg` the mobile layout prints
	 * `description` a second time in its place.
	 */
	description2?: string;
	/** More paragraphs, after `description2`; line breaks in the text are kept. */
	descriptionArray?: string[];
	/** From `lg` up: the left bullet column. Below `lg`: the first list. */
	benefitsSection?: BulletPoints;
	/** From `lg` up: the right bullet column. Below `lg`: the second list. */
	capabilitiesSection?: BulletPoints;
	/**
	 * Label of the call-to-action link. The untyped `pageContent` objects that
	 * `../BasePage/BasePage.tsx` reads pass a two-element array instead, which `ButtonLink` draws
	 * as two lines.
	 */
	actionText: string;
	/**
	 * Call-to-action target, split at `#` into path and hash: `/contact-us#quoteRequest` opens
	 * the contact form on the quote-request topic
	 * (`components/ContactFormWithFileAttacher/ContactFormWithFile.Functions.tsx` →
	 * `getTopicFromHash`). Absent: the link points at `#`.
	 */
	actionLink?: string;
	/** Slide photo, a site-relative path. */
	imageUrl: string;
};

/** Props of {@link TextImageSlide}. */
export interface TextImageSlideProps {
	/** Section title: the desktop `h1` and, on the pages, the `AllSections` title and anchor. */
	title: string;
	/** Sub-navigation label. Not read by this component; the pages pass it to `SubNavigation`. */
	navLabel: string;
	sections: Section[];
}

/** The equipment and process slides; see the file header. */
const TextImageSlide: React.FC<TextImageSlideProps> = (props) => {
	return (
		<>
			<Media greaterThanOrEqual="lg">
				<TextImageSlideDesktop {...props} />
			</Media>
			<Media lessThan="lg">
				<TextImageSlideMobile {...props} />
			</Media>
		</>
	);
};

export default TextImageSlide;
